import React, { useState } from 'react';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChangeViewerProps {
  changes: Record<string, { old: any; new: any }>;
  mode?: 'inline' | 'side-by-side';
}

// Format value for display
const formatValue = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.length === 0 ? '[]' : `[${value.join(', ')}]`;
    }
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

// Capitalize field name
const formatFieldName = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Get value type for styling
const getValueType = (value: any): string => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'object') return 'object';
  return 'string';
};

export const ChangeViewer: React.FC<ChangeViewerProps> = ({
  changes,
  mode = 'inline',
}) => {
  const [viewMode, setViewMode] = useState<'inline' | 'side-by-side'>(mode);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleField = (field: string) => {
    setExpandedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(field)) {
        newSet.delete(field);
      } else {
        newSet.add(field);
      }
      return newSet;
    });
  };

  const entries = Object.entries(changes);

  if (entries.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">No changes recorded</div>
    );
  }

  return (
    <div className="space-y-3">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => setViewMode('inline')}
          className={cn(
            'px-2 py-1 text-xs rounded',
            viewMode === 'inline'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-500 hover:bg-gray-100'
          )}
        >
          Inline
        </button>
        <button
          onClick={() => setViewMode('side-by-side')}
          className={cn(
            'px-2 py-1 text-xs rounded',
            viewMode === 'side-by-side'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-500 hover:bg-gray-100'
          )}
        >
          Side by Side
        </button>
      </div>

      {/* Changes List */}
      <div className="space-y-2">
        {entries.map(([field, { old: oldValue, new: newValue }]) => {
          const isExpanded = expandedFields.has(field);
          const oldFormatted = formatValue(oldValue);
          const newFormatted = formatValue(newValue);
          const isLongValue = oldFormatted.length > 50 || newFormatted.length > 50;

          return (
            <div
              key={field}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Field Header */}
              <div
                className={cn(
                  'flex items-center justify-between px-3 py-2 bg-gray-50',
                  isLongValue && 'cursor-pointer hover:bg-gray-100'
                )}
                onClick={() => isLongValue && toggleField(field)}
              >
                <span className="text-sm font-medium text-gray-700">
                  {formatFieldName(field)}
                </span>
                {isLongValue && (
                  <button className="p-1">
                    {isExpanded ? (
                      <EyeOff className="w-3 h-3 text-gray-400" />
                    ) : (
                      <Eye className="w-3 h-3 text-gray-400" />
                    )}
                  </button>
                )}
              </div>

              {/* Values */}
              <div className={cn('p-3', viewMode === 'side-by-side' && 'grid grid-cols-2 gap-3')}>
                {viewMode === 'inline' ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Old Value */}
                    <div className={cn(
                      'px-2 py-1 rounded text-sm',
                      'bg-red-50 text-red-700 line-through'
                    )}>
                      <span className={cn(
                        'font-mono text-xs',
                        getValueType(oldValue) === 'null' && 'italic'
                      )}>
                        {isExpanded || !isLongValue
                          ? oldFormatted
                          : oldFormatted.substring(0, 50) + '...'}
                      </span>
                    </div>

                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />

                    {/* New Value */}
                    <div className={cn(
                      'px-2 py-1 rounded text-sm',
                      'bg-green-50 text-green-700'
                    )}>
                      <span className={cn(
                        'font-mono text-xs',
                        getValueType(newValue) === 'null' && 'italic'
                      )}>
                        {isExpanded || !isLongValue
                          ? newFormatted
                          : newFormatted.substring(0, 50) + '...'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Old Value - Side by Side */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Old Value</div>
                      <div className={cn(
                        'px-2 py-1 rounded text-sm',
                        'bg-red-50 text-red-700'
                      )}>
                        <pre className={cn(
                          'font-mono text-xs whitespace-pre-wrap break-words',
                          getValueType(oldValue) === 'null' && 'italic'
                        )}>
                          {isExpanded || !isLongValue
                            ? oldFormatted
                            : oldFormatted.substring(0, 50) + '...'}
                        </pre>
                      </div>
                    </div>

                    {/* New Value - Side by Side */}
                    <div>
                      <div className="text-xs text-gray-500 mb-1">New Value</div>
                      <div className={cn(
                        'px-2 py-1 rounded text-sm',
                        'bg-green-50 text-green-700'
                      )}>
                        <pre className={cn(
                          'font-mono text-xs whitespace-pre-wrap break-words',
                          getValueType(newValue) === 'null' && 'italic'
                        )}>
                          {isExpanded || !isLongValue
                            ? newFormatted
                            : newFormatted.substring(0, 50) + '...'}
                        </pre>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
